import React, { useState, useEffect, useCallback } from "react";
import axios from '../../../../api/apiClient';
import {
	CreateTableNew as CreateTable,
	SearchBar,
	Button,
	GeneralMessage,
	useGeneralMessage
} from '../../../../common/APICommon';
import { LoadingSpinner } from '../../../../common/Loading';
import { token, domain } from '../../../Security';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { EmployeeRequestModal } from '../forms/EmployeeRequestModal';
import { formatDateLong } from "../../../../utils/textUtils";
import { getStatusConfig, WorkflowStatus } from "../../../../utils/enums";

export const Requester = ({ user }) => {
	const [loading, setLoading] = useState(false);
	const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

	const [data, setData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [selectedData, setSelectedData] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	const [showForm, setShowForm] = useState(false);
	const [mode, setMode] = useState("add"); // "add", "edit", "view"

	const apiUrl = domain + "/api/EmployeeRequest";

	const headers = [
		"ID",
		"Request Number",
		"Employee No",
		"Requested Employee",
		"Approver",
		"Vessel",
		"Title",
		"Request Date",
		"Status",
		"Actions"
	];

	const accessors = [
		"id",
		"requestNumber",
		"employeePersonalInformationEmployeeId",
		"employeePersonalInformationFullName",
		"approverEmployeePersonalInformationFullName",
		"vesselName",
		"title",
		"requestDate",
		"status",
		"actions"
	];
	const fetchData = useCallback(async () => {
		try {
			setLoading(true);

			// Get the ID of the logged-in user
			const currentUserId = user?.employeePersonalInformationId;

			// Send the request filtered by requesterId
			const response = await axios.get(`${apiUrl}?requesterId=${currentUserId}`, {
				headers: { 'Authorization': `Bearer ${token}` }
			});

			setData(response.data);
			setFilteredData(response.data);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	}, [user, apiUrl]); // Added dependencies

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleFilter = (value) => {
		setCurrentPage(1);
		const lowercaseValue = value.toLowerCase();
		const res = filteredData.filter((f) =>
			f?.requestNumber?.toLowerCase().includes(lowercaseValue) ||
			f?.employeeName?.toLowerCase().includes(lowercaseValue) ||
			f?.employeeNo?.toLowerCase().includes(lowercaseValue)
		);
		setData(res);
	};

	const openAddForm = () => {
		setSelectedData(null);
		setMode("add");
		setShowForm(true);
	};

	const openEditForm = (item) => {
		setSelectedData(item);
		setMode("edit");
		setShowForm(true);
	};

	const openViewForm = (item) => {
		setSelectedData(item);
		setMode("view");
		setShowForm(true);
	};

	const actions = [
		{
			name: "Edit",
			icon: <EditIcon fontSize="small" />,
			className: "bg-green-500 hover:bg-green-600 flex items-center",
			function: openEditForm,
			visible: (row) => row.status === WorkflowStatus.PENDING,
		},
		{
			name: "View",
			icon: <VisibilityIcon fontSize="small" />,
			className: "bg-blue-500 hover:bg-blue-600 flex items-center",
			function: openViewForm,
			visible: true,
		},
	];

	const renderCustomCell = (row, accessor) => {
		if (accessor === 'status') {
			const config = getStatusConfig(row.status);

			return (
				<span className={`px-3 py-1 text-[11px] rounded-full font-bold border uppercase tracking-wide
        ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
					{row.status}
				</span>
			);
		}

		if (accessor === 'requestDate') {
			return formatDateLong(row.requestDate);
		}
		return null;
	};

	return (
		<div>
			<SearchBar handleFilter={handleFilter} />

			<Button
				icon={<AddIcon />}
				name={"Create Request"}
				className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer mb-2"}
				onClick={openAddForm}
			/>

			<CreateTable
				tableName={"EmployeeRequests"}
				headers={headers}
				accessors={accessors}
				data={data}
				actions={actions}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				itemsPerPage={itemsPerPage}
				totalItems={data?.length}
				customCellRenderer={renderCustomCell}
			/>

			<EmployeeRequestModal
				isOpen={showForm}
				onClose={() => { setShowForm(false); fetchData(); }}
				mode={mode}
				selectedData={selectedData}
				showGeneralMessage={showGeneralMessage}
				user={user} // Pass the user object here
			/>

			<LoadingSpinner loading={loading} />

			<GeneralMessage
				form={showMessage}
				message={message}
				isSuccess={isSuccess}
			/>
		</div>
	);
};